import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { User, Mail, Phone, Lock, ArrowRight, Loader2, AlertCircle, CheckCircle2, Home } from 'lucide-react';
import { registerClient, clearMessage } from '../Redux/Actions/ClientActions';
import './ClientAuth.css';

const ClientRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const clientAuthState = useSelector(state => state.clientReducer) || {};
  const {
    loading = false,
    isAuthenticated = false,
    message = null,
    error = null
  } = clientAuthState;

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/my-points');
      dispatch(clearMessage()); // On nettoie les messages au passage
    }

    if (message || error) {
      const timer = setTimeout(() => {
        dispatch(clearMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, message, error, navigate, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est requis';
    if (!formData.password) newErrors.password = 'Le mot de passe est requis';
    else if (formData.password.length < 6) newErrors.password = 'Minimum 6 caractères';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'Vous devez accepter les conditions';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      const result = await dispatch(registerClient({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      }));

      // Si succès, on peut "rafraîchir" le formulaire (le vider)
      if (result && result.success) {
        setFormData({
          name: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          acceptTerms: false
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="auth-mobile-container">
      <div className="auth-content-wrapper">
        <header className="auth-brand-section">
          <div className="auth-logo-badge" style={{ background: 'var(--gold)' }}>
            <User className="text-white w-10 h-10" />
          </div>
          <h1 className="auth-title">Créer un compte</h1>
          <p className="auth-subtitle">Rejoignez notre programme de fidélité <br /> et profitez de cadeaux exclusifs.</p>
        </header>

        <main className="auth-card">
          {message && message.type !== 'success' && (
            <div className={`auth-banner ${message.type === 'success' ? 'success' : 'error'}`}>
              {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
              <span>{message.text}</span>
            </div>
          )}

          {error && !message && (
            <div className="auth-banner error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="auth-form-group">
              <label className="auth-label">Nom complet</label>
              <div className="auth-input-wrapper">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jean Dupont"
                  className="auth-input"
                  disabled={loading}
                />
                <User className="auth-input-icon" size={18} />
              </div>
              {errors.name && <p className="text-destructive text-xs ml-2 mt-1">{errors.name}</p>}
            </div>

            <div className="grid-mobile-12">
              <div className="auth-form-group">
                <label className="auth-label">Email</label>
                <div className="auth-input-wrapper">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.com"
                    className="auth-input"
                    disabled={loading}
                  />
                  <Mail className="auth-input-icon" size={18} />
                </div>
                {errors.email && <p className="text-destructive text-xs ml-2 mt-1">{errors.email}</p>}
              </div>

              <div className="auth-form-group">
                <label className="auth-label">Téléphone</label>
                <div className="auth-input-wrapper">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+216 XX XXX XXX"
                    className="auth-input"
                    disabled={loading}
                  />
                  <Phone className="auth-input-icon" size={18} />
                </div>
                {errors.phone && <p className="text-destructive text-xs ml-2 mt-1">{errors.phone}</p>}
              </div>
            </div>

            <div className="grid-mobile-12">
              <div className="auth-form-group">
                <label className="auth-label">Mot de passe</label>
                <div className="auth-input-wrapper">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="auth-input"
                    disabled={loading}
                  />
                  <Lock className="auth-input-icon" size={18} />
                </div>
                {errors.password && <p className="text-destructive text-xs ml-2 mt-1">{errors.password}</p>}
              </div>

              <div className="auth-form-group">
                <label className="auth-label">Confirmation</label>
                <div className="auth-input-wrapper">
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="auth-input"
                    disabled={loading}
                  />
                  <Lock className="auth-input-icon" size={18} />
                </div>
                {errors.confirmPassword && <p className="text-destructive text-xs ml-2 mt-1">{errors.confirmPassword}</p>}
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 mt-2">
              <input
                type="checkbox"
                name="acceptTerms"
                id="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                style={{ marginTop: '3px' }}
                disabled={loading}
              />
              <label htmlFor="acceptTerms" className="text-xs text-muted-foreground leading-snug">
                J'accepte les <Link to="/terms" className="text-primary font-bold">conditions</Link> et la <Link to="/privacy" className="text-primary font-bold">politique de confidentialité</Link>.
              </label>
            </div>
            {errors.acceptTerms && <p className="text-destructive text-xs ml-2">{errors.acceptTerms}</p>}

            <button
              type="submit"
              disabled={loading}
              className="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  S'inscrire gratuitement
                  <ArrowRight size={20} className="ml-1" />
                </>
              )}
            </button>
          </form>

          <footer className="auth-footer">
            <p className="auth-footer-text">
              Déjà inscrit ?
              <Link to="/login-client" className="auth-link">
                Se connecter
              </Link>
            </p>
          </footer>
        </main>

        <section className="auth-advantages">
          <h5 className="font-bold text-sm mb-3 text-foreground">✨ Vos avantages :</h5>
          <div className="grid grid-mobile-12">
            {[
              "100 pts offerts",
              "Cumul sur chaque achat",
              "Cadeaux exclusifs",
              "Offres flashs"
            ].map((text, i) => (
              <div key={i} className="advantage-item">
                <CheckCircle2 size={16} className="advantage-icon" />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="text-center">
          <Link to="/" className="auth-back-home">
            <Home size={16} />
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientRegister;